import React, {FC, useRef, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  TeammateLicenseTypesEnum,
  TeammateModel,
} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {VisualWorkspaceTeammate} from '../../../../models/api/visualWorkspaceModel';
import {FindTeammate} from '../../../../store/data/teammates/teammatesSelectors';
import {WorkspaceDropReorderTeammateHandler} from '../../../../store/data/workspace/reorder/types';
import {TeammateModelMap} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {NavigationLineStandardIcon} from '../../../common/navigation/navigationLineStandardIcon';
import {WorkspacesEditorTeammatesDropdownContainer} from '../dropdowns/workspacesEditorTeammatesDropdownContainer';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {WorkspaceLineDropTarget} from '../line/workspaceLineDropTarget';
import {WorkspaceLineMore} from '../line/workspaceLineMore';
import {Workspace} from '../workspace';
import {ToggleDropdownTeammateHandler, workspacesEditorDropdownHeight} from './context/types';
import {ToggleTeammateHandler} from './context/workspacesEditorContext';
import {StyledEditModeLabelDiv} from './teammateWorkspaceEditor';
import {WorkspacesEditorTeammatesContainer} from './workspacesEditorTeammatesContainer';

/*
 * Props.
 */

export interface TeammatesWorkspaceEditorProps {
  panelId: InboxesPanelId;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpand: () => void;
  /** Current user license (full). */
  userLicenseType: TeammateLicenseTypesEnum;
  /** The list of teammates displayed in the workspace. */
  visualWorkspaceTeammates: readonly VisualWorkspaceTeammate[];
  /** Event handler to select/remove teammates in P1. */
  onToggleTeammate: ToggleTeammateHandler;
  /** Function to get TeammateModel from id. */
  findTeammate: FindTeammate;
  /** Handler to select/remove teammates in the dropdown. */
  onToggleDropdownTeammate: ToggleDropdownTeammateHandler;
  /** Map that stores all the teammates interacted with in dropdown. */
  teammateModelMap: TeammateModelMap;
  /** Whether workspace is alphabetized. */
  isAlphabetized: boolean;
  onDropReorderTeammate: WorkspaceDropReorderTeammateHandler;
}

/*
 * Component.
 */

/**
 * This component is adapted from <TeammatesWorkspace /> but for use in <WorkspacesEditor />.
 * @see TeammatesWorkspace
 */
export const TeammatesWorkspaceEditor: FC<TeammatesWorkspaceEditorProps> = (props) => {
  const {isExpanded, userLicenseType, onToggleExpand, panelId, visualWorkspaceTeammates} = props;

  const [shouldShowTeammatesDropdown, setShouldShowTeammatesDropdown] = useState<boolean>(false);
  const [teammatesDropdownSelectedIds, setTeammatesDropdownSelectedIds] = useState<number[]>([]);

  const onTeammatesClick = () => setShouldShowTeammatesDropdown(true);
  const onTeammatesDismiss = () => {
    setShouldShowTeammatesDropdown(false);
    setTeammatesDropdownSelectedIds([]);
  };
  const onToggleDropdownTeammate = (teammate: TeammateModel) => {
    props.onToggleDropdownTeammate(teammate);
    setTeammatesDropdownSelectedIds([...new Set(teammatesDropdownSelectedIds).add(teammate.id)]);
  };

  const addTeammatesRef = useRef<HTMLDivElement>(null);

  // Hide the workspace completely for non-full teammates.
  if (userLicenseType !== TeammateLicenseTypesEnum.FULL) {
    return null;
  }

  return (
    <Workspace isExpanded={isExpanded}>
      {/* Header. */}
      <WorkspaceHeader
        panelId={panelId}
        interactionId="teammates"
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpand}
        isEditingWorkspace
      >
        <Trans id="workspace-assigned-title">Assigned conversations</Trans>
      </WorkspaceHeader>

      {/* Workspace content. */}
      {isExpanded &&
        renderTeammateList(props, {addTeammatesRef, onTeammatesClick, shouldShowTeammatesDropdown})}

      {/* Dropdown. */}
      {shouldShowTeammatesDropdown && (
        <WorkspacesEditorTeammatesDropdownContainer
          anchor={addTeammatesRef}
          panelId={panelId}
          placement="right-start"
          onRequestClose={onTeammatesDismiss}
          displayedTeammateIds={visualWorkspaceTeammates.map((teammate) => teammate.id)}
          onToggleDropdownTeammate={onToggleDropdownTeammate}
          teammatesDropdownSelectedIds={teammatesDropdownSelectedIds}
          height={workspacesEditorDropdownHeight}
        />
      )}
    </Workspace>
  );
};

function renderTeammateList(
  props: TeammatesWorkspaceEditorProps,
  teammateButtonParams: TeammateButtonParams,
) {
  return (
    <>
      <WorkspacesEditorTeammatesContainer
        panelId={props.panelId}
        onToggleTeammate={props.onToggleTeammate}
        isAlphabetized={props.isAlphabetized}
        teammateModelMap={props.teammateModelMap}
        visualWorkspaceTeammates={props.visualWorkspaceTeammates}
        onDropReorderTeammate={props.onDropReorderTeammate}
      />
      {renderTeammateButton(teammateButtonParams)}
    </>
  );
}

interface TeammateButtonParams {
  onTeammatesClick: () => void;
  addTeammatesRef: React.RefObject<HTMLDivElement>;
  shouldShowTeammatesDropdown: boolean;
}

function renderTeammateButton(params: TeammateButtonParams) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <div ref={params.addTeammatesRef}>
          <WorkspaceLineDropTarget
            indentLevel={0}
            onClick={params.onTeammatesClick}
            shouldAlphabetizeWorkspace={false}
            workspaceDragItem={undefined}
            isSelected={params.shouldShowTeammatesDropdown}
          >
            {<NavigationLineStandardIcon iconName="assignAll" color={theme.palette.blue.shade40} />}
            <NavigationLineLabel>
              <StyledEditModeLabelDiv>
                <Trans id="edit-mode-add-teammates">Add teammates</Trans>
              </StyledEditModeLabelDiv>
            </NavigationLineLabel>
            <WorkspaceLineMore isEditingWorkspaceButton />
          </WorkspaceLineDropTarget>
        </div>
      )}
    </UserThemeConsumer>
  );
}
